<!--
	Reveal — the entrance. One IntersectionObserver per instance, fires once, then
	disconnects; nothing is left running after the element has arrived.

	Deliberately restrained: 12px of travel and an opacity fade, no scale, no
	blur, no bounce. At this size the motion registers as "the page is settling"
	rather than "something is animating at me", which is what lets it be used on
	most sections without the scroll feeling gimmicky.

	Renders the end state immediately when motion is disabled or JS never runs —
	the content is in the DOM either way, so no-JS readers and crawlers see it.
-->
<script lang="ts">
	import type { Snippet } from 'svelte';
	import { motionDisabled } from '$lib/motion/reduced';

	let {
		children,
		/** Stagger, in ms — for lists. Keep the whole run under ~250ms. */
		delay = 0,
		/** How far it travels in. */
		y = 12,
		/** Element to render — 'li' when the parent is a real list (axe's list rule:
		    ul/ol may only contain li). Class and styles are element-agnostic. */
		as = 'div',
		class: klass = ''
	}: {
		children: Snippet;
		delay?: number;
		y?: number;
		as?: 'div' | 'li';
		class?: string;
	} = $props();

	let el: HTMLElement;
	let shown = $state(false);

	$effect(() => {
		if (motionDisabled()) {
			shown = true;
			return;
		}
		const io = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					shown = true;
					io.disconnect();
				}
			},
			// Fire a little before the edge, so it's already settling as it arrives.
			{ rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
		);
		io.observe(el);
		return () => io.disconnect();
	});
</script>

<svelte:element
	this={as}
	bind:this={el}
	class="reveal {klass}"
	class:reveal--in={shown}
	style:--reveal-delay="{delay}ms"
	style:--reveal-y="{y}px"
>
	{@render children()}
</svelte:element>

<style>
	.reveal {
		opacity: 0;
		transform: translateY(var(--reveal-y));
		transition:
			opacity 520ms var(--ease-out) var(--reveal-delay),
			transform 520ms var(--ease-out) var(--reveal-delay);
	}
	.reveal--in {
		opacity: 1;
		transform: none;
	}

	/* Belt and braces: the component already checks motionDisabled(), but if the
	   preference flips after mount, the CSS still holds the end state. */
	@media (prefers-reduced-motion: reduce) {
		.reveal {
			opacity: 1;
			transform: none;
			transition: none;
		}
	}
</style>
